import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Search, ShoppingCart, RefreshCw, FolderCheck, MailCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { PO_API, poAuth, money, fmtPoDate, INPUT, BTN_PRIMARY, BTN_GHOST } from './po/poUtils';
import POForm from './po/POForm';
import PODetail from './po/PODetail';

const useQuery = () => new URLSearchParams(useLocation().search);

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const query = useQuery();
  const [perm, setPerm] = useState(null);
  const [q, setQ] = useState(query.get('q') || '');
  const [wo, setWo] = useState(query.get('wo') || '');
  const [mineOnly, setMineOnly] = useState(false);
  const [data, setData] = useState({ rows: [], total: 0, sum_total: 0, sum_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | {po?: object}
  const [open, setOpen] = useState(query.get('po') || null);
  const [years, setYears] = useState({ current: '', years: [] });
  const [year, setYear] = useState('');

  useEffect(() => { axios.get(`${PO_API}/permissions`, poAuth()).then(r => setPerm(r.data)).catch(() => setPerm({ can_create: false })); }, []);
  useEffect(() => { axios.get(`${PO_API}/years`, poAuth()).then(r => { setYears(r.data); setYear(y => y || r.data.current); }).catch(() => {}); }, []);
  const search = useLocation().search;
  useEffect(() => { if (new URLSearchParams(search).get('new') === '1' && perm?.can_create) { setForm({}); navigate('/purchase-orders', { replace: true }); } }, [perm, search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(PO_API, { ...poAuth(), params: { q, wo, ordered_by: mineOnly ? (perm?.full_name || '') : '', limit: 400, year: (q || wo) ? 'all' : year } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load purchase orders'); }
    setLoading(false);
  }, [q, wo, mineOnly, perm, year]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const onSaved = (po) => { setForm(null); load(); setOpen(po.po_number); };

  return (
    <div className="ops-canvas -m-3 lg:-m-6 p-4 lg:p-6 min-h-full text-slate-800 space-y-4" data-testid="purchase-orders-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="text-2xl font-black tracking-tight text-slate-900 flex items-center gap-2"><ShoppingCart className="w-6 h-6 text-sky-700" /> Purchase Orders</h1>
            <div className="inline-flex items-center rounded-full border border-sky-200 bg-white/80 p-0.5" data-testid="po-year-pills">
              {years.years.map(y => (
                <button key={y.year} onClick={() => setYear(y.year)} className={`h-7 px-3 rounded-full text-xs font-bold transition-colors ${year === y.year ? 'bg-slate-900 text-white' : 'text-sky-800 hover:bg-sky-50'}`} data-testid={`po-year-${y.year}`}>{y.long} book</button>
              ))}
              <button onClick={() => setYear('all')} className={`h-7 px-3 rounded-full text-xs font-bold transition-colors ${year === 'all' ? 'bg-slate-900 text-white' : 'text-sky-800 hover:bg-sky-50'}`} data-testid="po-year-all">All</button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-1">Same list as the <b>{year && year !== 'all' ? `20${year}` : ''} Purchase Orders</b> sheet{year && year !== 'all' ? '' : 's'} — raise it here or there, both stay in step (sheet checked every 2 minutes).{years.current && year && year !== 'all' && year !== years.current ? ` New POs are claimed from the 20${years.current} book.` : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className={BTN_GHOST} title="Refresh" data-testid="po-refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
          {perm?.can_create && <button onClick={() => setForm({})} className={BTN_PRIMARY} data-testid="po-new-btn"><Plus className="w-4 h-4" /> New PO</button>}
        </div>
      </div>

      <div className="ti-panel rounded-2xl p-3 flex items-center gap-2 flex-wrap" data-testid="po-filters">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search PO #, vendor, description, who ordered…" className={INPUT + ' pl-9'} data-testid="po-search" />
        </div>
        <input value={wo} onChange={e => setWo(e.target.value)} placeholder="Work order (26-3092)" className={INPUT + ' w-44 font-mono'} data-testid="po-wo-filter" />
        {perm?.can_create && (
          <button onClick={() => setMineOnly(m => !m)} className={`h-9 px-3 rounded-lg text-xs font-bold border transition-colors ${mineOnly ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`} data-testid="po-mine-toggle">My POs</button>
        )}
        {(q || wo || mineOnly) && <button onClick={() => { setQ(''); setWo(''); setMineOnly(false); }} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="po-clear-filters"><X className="w-3.5 h-3.5" /> clear</button>}
        <div className="ml-auto text-xs text-slate-600 flex items-center gap-4" data-testid="po-totals">
          <span><b className="text-slate-900">{data.total}</b> POs</span>
          <span>Subtotal <b className="text-slate-900 tabular-nums">{money(data.sum_amount)}</b></span>
          <span>Total <b className="text-sky-800 tabular-nums">{money(data.sum_total)}</b></span>
        </div>
      </div>

      <div className="ti-panel-solid rounded-2xl overflow-hidden" data-testid="po-table-panel">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/90 border-b border-slate-200">
                <th className="text-left px-3 py-2 font-bold">PO #</th>
                <th className="text-left px-3 py-2 font-bold">Date</th>
                <th className="text-left px-3 py-2 font-bold">WO</th>
                <th className="text-left px-3 py-2 font-bold">Company</th>
                <th className="text-left px-3 py-2 font-bold">Description</th>
                <th className="text-left px-3 py-2 font-bold hidden lg:table-cell">Contact</th>
                <th className="text-right px-3 py-2 font-bold hidden md:table-cell">Amount</th>
                <th className="text-right px-3 py-2 font-bold hidden md:table-cell">Tax</th>
                <th className="text-right px-3 py-2 font-bold">Total</th>
                <th className="text-left px-3 py-2 font-bold hidden lg:table-cell">Ordered by</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.map(r => (
                <tr key={r.po_number} onClick={() => setOpen(r.po_number)} className="hover:bg-sky-50/70 cursor-pointer transition-colors" data-testid={`po-row-${r.po_number}`}>
                  <td className="px-3 py-2 font-mono font-bold text-slate-900">{r.po_number}</td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtPoDate(r.date)}</td>
                  <td className="px-3 py-2 font-mono text-sky-800 font-semibold">{r.work_order_number || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 text-slate-900 font-medium max-w-[180px] truncate">{r.company_name}</td>
                  <td className="px-3 py-2 text-slate-600 max-w-[320px] truncate">{r.description}</td>
                  <td className="px-3 py-2 text-slate-500 hidden lg:table-cell max-w-[140px] truncate">{r.vendor_contact}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700 hidden md:table-cell">{money(r.amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500 hidden md:table-cell">{money(r.tax)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{money(r.total_amount)}</td>
                  <td className="px-3 py-2 text-slate-600 hidden lg:table-cell max-w-[140px] truncate">{r.ordered_by}</td>
                  <td className="px-2 py-2 text-slate-400 whitespace-nowrap">
                    {r.drive_url && <FolderCheck className="w-3.5 h-3.5 inline text-emerald-600" title="Filed in Drive" />}
                    {r.emailed_at && <MailCheck className="w-3.5 h-3.5 inline text-emerald-600 ml-1" title={`Emailed to ${r.emailed_to}`} />}
                  </td>
                </tr>
              ))}
              {!loading && data.rows.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-sm text-slate-500" data-testid="po-empty">No purchase orders match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {form && <POForm po={form.po} meName={perm?.full_name} onClose={() => setForm(null)} onSaved={onSaved} />}
      {open && !form && <PODetail poNumber={open} canCreate={!!perm?.can_create} onClose={() => setOpen(null)} onEdit={(po) => setForm({ po })} onChanged={load} />}
    </div>
  );
}
